import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { createOfficeTask, getOfficeTasks, uploadOfficeTaskPhoto } from "../../../actions/officeTask.actions";
import {
    closeOfficeTaskCreateModal,
} from "../../../actions/officeTaskCreateModal.actions";
import { getOfficeMembers } from "../../../actions/officeMember.actions";
import { getOfficeOrders } from "../../../actions/officeOrder.actions";
import { getMaterials, getWarehouses } from "../../../actions/inventory.actions";
import { listOfficeProducts } from "../../../actions/productOffice.actions";
import TaskDueDatePicker from "./TaskDueDatePicker";

import "../../../scss/adminOffice/components/office-tasks-AO.scss";

const PRIORITY = {
    low: "Низкий",
    normal: "Обычный",
    high: "Высокий",
    urgent: "Срочно",
};

const idOf = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;

    return String(value._id || value.id || value.userId?._id || value.userId || "");
};

const memberUser = (member) => member?.userId || member?.user || {};

const userName = (user = {}) => (
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    user.email ||
    user.username ||
    "Сотрудник"
);

const safePromise = (value) => {
    if (value && typeof value.catch === "function") {
        value.catch(() => null);
    }
};

const makeEmptyForm = (prefill = {}) => {
    const links = prefill.links || {};

    return {
        title: String(prefill.title || ""),
        description: String(prefill.description || ""),
        priority: ["low", "normal", "high", "urgent"].includes(prefill.priority)
            ? prefill.priority
            : "normal",
        dueAt: String(prefill.dueAt || ""),
        assignmentType: ["person", "multiple", "board"].includes(prefill.assignmentType)
            ? prefill.assignmentType
            : "person",
        assigneeIds: Array.isArray(prefill.assigneeIds)
            ? prefill.assigneeIds.map(String).filter(Boolean)
            : [],
        checklistText: Array.isArray(prefill.checklist)
            ? prefill.checklist.map((item) => String(item?.text || item || "").trim()).filter(Boolean).join("\n")
            : String(prefill.checklistText || ""),
        orderId: String(links.orderId || prefill.orderId || ""),
        productId: String(links.productId || prefill.productId || ""),
        warehouseId: String(links.warehouseId || prefill.warehouseId || ""),
        materialId: String(links.materialId || prefill.materialId || ""),
    };
};

function getPortalTarget() {
    if (typeof document === "undefined") return null;

    return document.querySelector(".admin-app-AO") || document.body;
}

export default function GlobalOfficeTaskCreateModal() {
    const dispatch = useDispatch();

    const modal = useSelector((state) => state.officeTaskCreateModal || {}, shallowEqual);
    const taskState = useSelector((state) => state.officeTask || {}, shallowEqual);
    const { membersByOffice = {} } = useSelector((state) => state.officeMember || {}, shallowEqual);

    const orders = useSelector((state) => state.officeOrder?.orders || [], shallowEqual);
    const products = useSelector((state) => state.productOffice?.office || [], shallowEqual);
    const warehouses = useSelector((state) => state.inventory?.warehouses || [], shallowEqual);
    const materials = useSelector((state) => state.inventory?.materials || [], shallowEqual);

    const {
        open,
        officeId,
        source = "",
        sourceTitle = "",
        prefill = {},
        openedAt,
    } = modal;

    const [portalTarget, setPortalTarget] = useState(null);
    const [form, setForm] = useState(makeEmptyForm);
    const [referenceFiles, setReferenceFiles] = useState([]);
    const [error, setError] = useState("");

    const cleanOfficeId = officeId && officeId !== "all" ? String(officeId) : "";
    const busy = !!taskState.actionLoading;

    const members = useMemo(() => (
        (membersByOffice?.[cleanOfficeId] || []).filter((item) => (
            item?.isActive !== false &&
            item?.permissions?.tasks !== false
        ))
    ), [membersByOffice, cleanOfficeId]);

    const orderOptions = useMemo(() => {
        const result = [...orders];
        const prefillId = String(prefill?.links?.orderId || prefill?.orderId || "");

        if (prefillId && !result.some((item) => idOf(item) === prefillId)) {
            result.unshift({
                _id: prefillId,
                orderNumber: prefill?.links?.orderNumber || prefill?.orderNumber || `Заказ ${prefillId.slice(-6)}`,
            });
        }

        return result;
    }, [orders, prefill]);

    const productOptions = useMemo(() => {
        const result = [...products];
        const prefillId = String(prefill?.links?.productId || prefill?.productId || "");

        if (prefillId && !result.some((item) => idOf(item) === prefillId)) {
            result.unshift({
                _id: prefillId,
                name: prefill?.links?.productName || prefill?.productName || `Товар ${prefillId.slice(-6)}`,
                article: prefill?.links?.article || prefill?.article || "",
            });
        }

        return result;
    }, [products, prefill]);

    useEffect(() => {
        setPortalTarget(getPortalTarget());
    }, []);

    useEffect(() => {
        if (!open) return;

        setForm(makeEmptyForm(prefill));
        setReferenceFiles([]);
        setError("");
    }, [open, openedAt, prefill]);

    useEffect(() => {
        if (!open || !cleanOfficeId) return;

        safePromise(dispatch(getOfficeMembers(cleanOfficeId)));
        safePromise(dispatch(getOfficeOrders({
            officeId: cleanOfficeId,
            mode: "office",
            limit: 100,
        })));
        safePromise(dispatch(listOfficeProducts(cleanOfficeId)));
        safePromise(dispatch(getWarehouses(cleanOfficeId)));
        safePromise(dispatch(getMaterials({
            officeId: cleanOfficeId,
            scope: "all",
        })));
    }, [dispatch, open, cleanOfficeId]);

    useEffect(() => {
        if (!open || typeof document === "undefined") return undefined;

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape" && !busy) {
                dispatch(closeOfficeTaskCreateModal());
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [dispatch, open, busy]);

    if (!open || !portalTarget) return null;

    const close = () => {
        if (busy) return;
        dispatch(closeOfficeTaskCreateModal());
    };

    const toggleAssignee = (userId) => {
        setForm((prev) => {
            const id = String(userId);
            const exists = prev.assigneeIds.includes(id);

            if (prev.assignmentType === "person") {
                return {
                    ...prev,
                    assigneeIds: exists ? [] : [id],
                };
            }

            return {
                ...prev,
                assigneeIds: exists
                    ? prev.assigneeIds.filter((item) => item !== id)
                    : [...prev.assigneeIds, id],
            };
        });
    };

    const submit = async () => {
        setError("");

        if (!cleanOfficeId) {
            setError("Не найден выбранный офис.");
            return;
        }

        if (!form.title.trim()) {
            setError("Введите название задания.");
            return;
        }

        if (form.assignmentType !== "board" && !form.assigneeIds.length) {
            setError("Выберите исполнителя или общую доску.");
            return;
        }

        const checklist = form.checklistText
            .split("\n")
            .map((text) => text.trim())
            .filter(Boolean)
            .slice(0, 30)
            .map((text) => ({ text }));

        const warehouse = warehouses.find((item) => idOf(item) === form.warehouseId);
        const material = materials.find((item) => idOf(item) === form.materialId);

        try {
            const created = await dispatch(createOfficeTask(cleanOfficeId, {
                title: form.title.trim(),
                description: form.description.trim(),
                priority: form.priority,
                dueAt: form.dueAt || null,
                assignmentType: form.assignmentType,
                assigneeIds: form.assignmentType === "board" ? [] : form.assigneeIds,
                checklist,
                links: {
                    orderId: form.orderId,
                    productId: form.productId,
                    warehouseId: form.warehouseId,
                    materialId: form.materialId,
                    warehouseName: warehouse?.name || warehouse?.title || "",
                    materialName: material?.name || material?.title || material?.code || "",
                },
            }));

            const createdTaskId = idOf(created?.task);

            if (createdTaskId && referenceFiles.length) {
                for (const file of referenceFiles) {
                    try {
                        await dispatch(uploadOfficeTaskPhoto(
                            cleanOfficeId,
                            createdTaskId,
                            file,
                            "reference"
                        ));
                    } catch (photoError) {
                        console.error("Не удалось загрузить фото к созданному заданию:", photoError);
                    }
                }
            }

            dispatch(closeOfficeTaskCreateModal());
            safePromise(dispatch(getOfficeTasks({ officeId: cleanOfficeId })));
        } catch (requestError) {
            setError(
                requestError?.response?.data?.error ||
                requestError?.response?.data?.message ||
                requestError?.message ||
                "Не удалось создать задание."
            );
        }
    };

    const modalNode = (
        <div
            className="office-task-modal-AO office-task-global-create-AO"
            role="dialog"
            aria-modal="true"
            onMouseDown={close}
        >
            <section onMouseDown={(event) => event.stopPropagation()}>
                <header>
                    <div>
                        <span className="office-task-global-create-AO__eyebrow">
                            {sourceTitle || "Новое задание"}
                        </span>
                        <h2>Создать задание</h2>
                        <p>
                            {source === "order"
                                ? "Заказ уже привязан. Остаётся описать работу и выбрать исполнителя."
                                : "Модалку можно вызывать из любого раздела и передавать нужную связь заранее."}
                        </p>
                    </div>
                    <button type="button" onClick={close} disabled={busy}>×</button>
                </header>

                <div className="office-task-modal-AO__content">
                    <div className="office-task-modal-AO__grid">
                        <label className="is-wide">
                            <span>Название</span>
                            <input
                                autoFocus
                                value={form.title}
                                maxLength={180}
                                placeholder="Например: подготовить памятник к отгрузке"
                                onChange={(event) => setForm((prev) => ({
                                    ...prev,
                                    title: event.target.value,
                                }))}
                            />
                        </label>

                        <label className="is-wide">
                            <span>Описание</span>
                            <textarea
                                rows={4}
                                maxLength={4000}
                                value={form.description}
                                placeholder="Что нужно сделать, на что обратить внимание..."
                                onChange={(event) => setForm((prev) => ({
                                    ...prev,
                                    description: event.target.value,
                                }))}
                            />
                        </label>

                        <label>
                            <span>Приоритет</span>
                            <select
                                value={form.priority}
                                onChange={(event) => setForm((prev) => ({
                                    ...prev,
                                    priority: event.target.value,
                                }))}
                            >
                                {Object.entries(PRIORITY).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </label>

                        <label>
                            <span>Срок</span>
                            <TaskDueDatePicker
                                value={form.dueAt}
                                disabled={busy}
                                onChange={(dueAt) => setForm((prev) => ({
                                    ...prev,
                                    dueAt,
                                }))}
                            />
                        </label>
                    </div>

                    <div className="office-task-modal-AO__block">
                        <h3>Кому выдать</h3>

                        <div className="office-task-modal-AO__types">
                            {[
                                ["person", "Одному"],
                                ["multiple", "Нескольким"],
                                ["board", "На общую доску"],
                            ].map(([key, label]) => (
                                <button
                                    type="button"
                                    key={key}
                                    className={form.assignmentType === key ? "is-active" : ""}
                                    onClick={() => setForm((prev) => ({
                                        ...prev,
                                        assignmentType: key,
                                        assigneeIds: key === "board"
                                            ? []
                                            : key === "person"
                                                ? prev.assigneeIds.slice(0, 1)
                                                : prev.assigneeIds,
                                    }))}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {form.assignmentType === "board" ? (
                            <p className="office-task-modal-AO__hint">
                                Первый сотрудник, который нажмёт «Взять», закрепит задание за собой.
                            </p>
                        ) : (
                            <div className="office-task-modal-AO__members">
                                {members.map((member) => {
                                    const user = memberUser(member);
                                    const userId = idOf(user);
                                    const selected = form.assigneeIds.includes(userId);

                                    return (
                                        <button
                                            type="button"
                                            key={userId}
                                            className={selected ? "is-selected" : ""}
                                            onClick={() => toggleAssignee(userId)}
                                        >
                                            <b>{userName(user)}</b>
                                            <small>{user.email || user.username || member.role || ""}</small>
                                            <i>{selected ? "✓" : ""}</i>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="office-task-modal-AO__block">
                        <h3>Связать с работой офиса</h3>

                        <div className="office-task-modal-AO__grid">
                            <label>
                                <span>Заказ</span>
                                <select
                                    value={form.orderId}
                                    onChange={(event) => setForm((prev) => ({
                                        ...prev,
                                        orderId: event.target.value,
                                    }))}
                                >
                                    <option value="">Не привязывать</option>
                                    {orderOptions.map((item) => (
                                        <option key={idOf(item)} value={idOf(item)}>
                                            {item.orderNumber || idOf(item)}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Товар</span>
                                <select
                                    value={form.productId}
                                    onChange={(event) => setForm((prev) => ({
                                        ...prev,
                                        productId: event.target.value,
                                    }))}
                                >
                                    <option value="">Не привязывать</option>
                                    {productOptions.map((item) => (
                                        <option key={idOf(item)} value={idOf(item)}>
                                            {item.article ? `${item.article} · ` : ""}
                                            {item.fullName || item.name || "Товар"}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Склад</span>
                                <select
                                    value={form.warehouseId}
                                    onChange={(event) => setForm((prev) => ({
                                        ...prev,
                                        warehouseId: event.target.value,
                                    }))}
                                >
                                    <option value="">Не привязывать</option>
                                    {warehouses.map((item) => (
                                        <option key={idOf(item)} value={idOf(item)}>
                                            {item.name || item.title || "Склад"}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Материал</span>
                                <select
                                    value={form.materialId}
                                    onChange={(event) => setForm((prev) => ({
                                        ...prev,
                                        materialId: event.target.value,
                                    }))}
                                >
                                    <option value="">Не привязывать</option>
                                    {materials.map((item) => (
                                        <option key={idOf(item)} value={idOf(item)}>
                                            {item.name || item.title || item.code || "Материал"}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </div>

                    <div className="office-task-modal-AO__block">
                        <h3>Фото к заданию</h3>

                        <label className="office-task-global-create-AO__file-picker">
                            <span>
                                Можно сразу приложить исходные фото. Исполнитель потом добавит свой фотоотчёт.
                            </span>
                            <input
                                type="file"
                                multiple
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(event) => {
                                    const files = Array.from(event.target.files || []).slice(0, 10);
                                    event.target.value = "";
                                    setReferenceFiles(files);
                                }}
                            />
                        </label>

                        {referenceFiles.length ? (
                            <div className="office-task-global-create-AO__file-list">
                                {referenceFiles.map((file, index) => (
                                    <span key={`${file.name}-${file.size}-${index}`}>
                                        {file.name}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    <div className="office-task-modal-AO__block">
                        <h3>Чек-лист</h3>

                        <label>
                            <span>Один пункт на строку</span>
                            <textarea
                                rows={6}
                                value={form.checklistText}
                                placeholder={"Забрать комплект со склада\nПроверить размеры\nСделать фотоотчёт"}
                                onChange={(event) => setForm((prev) => ({
                                    ...prev,
                                    checklistText: event.target.value,
                                }))}
                            />
                        </label>
                    </div>

                    {error ? (
                        <div className="office-task-modal-AO__error">
                            {error}
                        </div>
                    ) : null}
                </div>

                <footer>
                    <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={close}
                        disabled={busy}
                    >
                        Отмена
                    </button>
                    <button
                        type="button"
                        className="btn btn--primary"
                        onClick={submit}
                        disabled={busy}
                    >
                        {busy ? "Создаём..." : "Опубликовать"}
                    </button>
                </footer>
            </section>
        </div>
    );

    return createPortal(modalNode, portalTarget);
}
